import React, { useState, useEffect } from 'react';
import { graphql, useStaticQuery } from "gatsby";
import ServicesLayout from '../../components/ServicesLayout';
import { Seo } from "../../components/seo"
import { HeroSection, TrustpilotSimple, Button } from '../../components';
import residencyHeaderImage from '../../images/residency-header.webp';
import visaIcon from '../../images/icons/route-icon.png';
import taxIcon from '../../images/icons/docs.png';
import educationIcon from '../../images/icons/education-icon.png';
import timeIcon from '../../images/icons/checked.png';
import backupIcon from '../../images/icons/backup.png';
import businessIcon from '../../images/icons/briefcase.png';
import { Http } from '@capacitor-community/http';

const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

const fetchImageUrl = async (mediaId) => {
    if (!mediaId) return '/default-image.jpg';
    try {
        const response = await Http.get({
            url: `https://holbornpass.com/wp-json/wp/v2/media/${mediaId}`,
        });
        if (response.status === 200) {
            return response.data.source_url;
        }
    } catch (error) {
        console.error(`Error fetching image with ID ${mediaId}:`, error.message || error);
    }
    return '/default-image.jpg';
};

const Residency = () => {
    const data = useStaticQuery(graphql`
        query {
            backgroundImage: file(relativePath: { eq: "residency-header.webp" }) {
                childImageSharp {
                    gatsbyImageData(layout: FULL_WIDTH)
                }
            },
            blueBackgroundImage: file(relativePath: { eq: "hero-bg-image.webp" }) {
                childImageSharp {
                    gatsbyImageData(layout: FULL_WIDTH)
                }
            },
        }
    `);

    const bgImageData = data.backgroundImage.childImageSharp.gatsbyImageData;
    const blueBgImageData = data.blueBackgroundImage.childImageSharp.gatsbyImageData;

    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await Http.get({
                    url: 'https://holbornpass.com/wp-json/wp/v2/country-programme?_embed&per_page=100',
                });

                if (response.status !== 200) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = response.data;
                const filteredCountries = data.filter(country => !country.acf.sold_out);

                const countriesWithImages = await Promise.all(filteredCountries.map(async (country) => {
                    const aboutImageUrl = await fetchImageUrl(country.acf.about_image);
                    return { ...country, aboutImageUrl };
                }));

                setCountries(countriesWithImages);
            } catch (error) {
                console.error('Error during fetch operation:', error.message || error);
                setError('Error fetching countries. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchCountries();
    }, []);

    const handleCountryClick = (country) => {
        const title = country.title.rendered ? country.title.rendered.toLowerCase().replace(/\s+/g, '-') : 'unknown-title';
        const countryUrl = `https://holbornpass.com/countries/${title}/`;
        window.open(countryUrl, '_blank');
    };

    const decodeHtmlEntities = (text) => {
        const textArea = document.createElement('textarea');
        textArea.innerHTML = text;
        return textArea.value;
    };

    return (
        <>
            <ServicesLayout>
                <HeroSection
                    title="Residency"
                    subtitle="Open up a world of possibilities through our dedicated programmes"
                    buttonText="Get financial advice"
                    bgImageData={bgImageData}
                    blueBgImageData={blueBgImageData}
                />
                <section className="services-content py-24">
                    <div className="grid grid-cols-12">
                        <div className="col-start-2 col-span-10 lg:col-span-8 lg:col-start-2">
                            <h2 className="font-semibold">Become a global citizen and enjoy the benefits</h2>
                            <p>Citizenship or residency by investment programmes are designed specifically for individuals and families who want to obtain a second passport or citizenship. This is done by investing funds in various ways in their preferred country.</p>
                            <p>Obtaining citizenship in one of the European Union or Caribbean countries whether for travel, settlement or business purposes, can work as a doorway to numerous countries across the world. Investing in residency can also be a quick solution in acquiring permanent residency, eventually helping you become a European Union citizen.</p>
                        </div>
                 
                    <div className="col-start-2 col-span-10 lg:col-span-5 mt-10 lg:col-start-2">
                        <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                        <iframe
                            src="https://customer-iclu53owqhlji7yh.cloudflarestream.com/9945d948a54fddb8d668a0bbd87f95ba/iframe?poster=https%3A%2F%2Fcustomer-iclu53owqhlji7yh.cloudflarestream.com%2F9945d948a54fddb8d668a0bbd87f95ba%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D12s%26height%3D600"
                            loading="lazy"
                            style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                            allowFullScreen
                            title="Residency Video"
                        />
                        </div>
                    </div>   
                </div>
                </section>
                <section className="bg-light-blue text-holborn-blue py-16">
                    <div className="grid grid-cols-12">
                        <div className="col-start-2 col-span-10 lg:col-start-2">
                            <h2 className="mb-4 font-semibold">What are the benefits of a second passport?</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                                <div className="border border-holborn-mid-blue p-6">
                                    <div className="flex">
                                        <img src={visaIcon} alt="Visa icon" style={{ width: "35px", height: "35px" }} className="mr-4" />
                                        <h5 className="font-semibold">Visa-free travel</h5>
                                    </div>
                                    <p>Getting a second passport from a European, Caribbean or Oceanic country can allow visa-free travel to 180+ countries across the globe.</p>
                                </div>
                                <div className="border border-holborn-mid-blue p-6">
                                    <div className="flex">
                                        <img src={taxIcon} alt="Tax icon" style={{ width: "35px", height: "35px" }} className="mr-4" />
                                        <h5 className="font-semibold">Tax benefits</h5>
                                    </div>
                                    <p>Most citizenships by investment programs are in low tax jurisdictions. Citizens can benefit from savings on Income Tax, Net Worth Tax, Inheritance Tax and Capital Gains Tax.</p>
                                </div>
                                <div className="border border-holborn-mid-blue p-6">
                                    <div className="flex">
                                        <img src={educationIcon} alt="Education icon" style={{ width: "35px", height: "35px" }} className="mr-4" />
                                        <h5 className="font-semibold">Education</h5>
                                    </div>
                                    <p>A second passport in a European country opens up a wealth of opportunities. You and your family can live, work and study in some of the world’s leading institutions.</p>
                                </div>
                                <div className="border border-holborn-mid-blue p-6">
                                    <div className="flex">
                                        <img src={timeIcon} alt="Time icon" style={{ width: "35px", height: "35px" }} className="mr-4" />
                                        <h5 className="font-semibold">Save time</h5>
                                    </div>
                                    <p>An investor visa can help you avoid the lengthy processes of visa applications and complications of travel to a wide variety of destinations.</p>
                                </div>
                                <div className="border border-holborn-mid-blue p-6">
                                    <div className="flex">
                                        <img src={backupIcon} alt="Backup icon" style={{ width: "35px", height: "35px" }} className="mr-4" />
                                        <h5 className="font-semibold">Backup plan</h5>
                                    </div>
                                    <p>Having a plan B is always a good idea. Gaining second citizenship through an investment visa allows you and your family the flexibility you need to travel at short notice.</p>
                                </div>
                                <div className="border border-holborn-mid-blue p-6">
                                    <div className="flex">
                                        <img src={businessIcon} alt="Business icon" style={{ width: "35px", height: "35px" }} className="mr-4"/>
                                        <h5 className="font-semibold">Business opportunities</h5>
                                    </div>
                                    <p>Applying for a passport by investment visa allows you to increase your business footprint on an international scale. Issues such as travel become more manageable, allowing you to focus on your global business goals.</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 pt-10">
                          <p>We want to help our clients who are looking for ways to expand their
                            businesses in tax-friendly environments or people who want to be able to travel
                            around the world without the hassle of visa requirements. Get in touch with our
                            advisers to learn how you can benefit from our wide range of second passport
                            programmes or head over to our website programmes or head over to our website <a href="https://www.holbornpass.com" target="_blank" rel="noopener noreferrer" className="font-semibold">www.holbornpass.com</a>.</p>
                        <p>For every investor looking for opportunities to lawfully obtain citizenship
                            or a second passport for better travel options, citizenship or residency by
                            investment programmes are the fastest and simplest way.</p>
                            </div>
                        </div>
                    </div>
                </section>
                <TrustpilotSimple />
                <section className="services-content py-20">
                    <div className="grid grid-cols-12">
                        <div className="col-start-2 col-span-12 lg:col-span-10 lg:col-start-2">
                            <h2 className="text-2xl font-semibold mb-6">Our CBI and RBI Programmes</h2>
                            {loading && <p className="text-center text-sm">Loading...</p>}
                            {error && <p className="text-center text-sm text-holborn-red font-bold">{error}</p>}
                            {!loading && !error && countries.length === 0 && <p className="text-center text-sm">No countries found.</p>}
                            {!loading && !error && countries.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                    {countries.map((country) => (
                                        <div key={country.id} className="bg-white p-4 rounded-lg">
                                            <div
                                                className="flex mb-4 cursor-pointer"
                                                onClick={() => handleCountryClick(country)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        handleCountryClick(country);
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={country.aboutImageUrl}
                                                    alt={country.title.rendered || 'Country'}
                                                    className="rounded-lg object-cover h-full"
                                                />
                                            </div>
                                            <div className="flex items-center">
                                                <h5 className="font-semibold text-holborn-blue">{decodeHtmlEntities(country.title.rendered)}</h5>
                                                <p className="text-holborn-dark-blue pb-2">, {country.acf.citizenship_or_residency.map(capitalizeFirstLetter).join(', ')}</p>
                                            </div>
                                            <p className="text-holborn-dark-blue pb-0">{country.acf.programme_short_intro}</p>
                                            <p className="text-holborn-green font-bold mb-2">Min Investment Amount: {country.acf.currency_symbol}{country.acf.min_investment_amount.toLocaleString()}</p>
                                            <div className="">
                                                <Button color="holborn-green" onClick={() => handleCountryClick(country)}>
                                                    More details
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </ServicesLayout>
        </>
    )
}

export const Head = () => (
    <Seo
        title="Residency"
        description="Getting a second passport allows you to take advantage of tax reduction, explore business opportunities along with studying and travel visa-free."
        image={residencyHeaderImage}
    >
    </Seo>
)

export default Residency
